/**
 * Mail-Schreibfenster.
 * Modi:
 *   /account/:id/compose                                  → leere Mail
 *   /account/:id/compose?reply=mailbox/uid                → Antwort
 *   /account/:id/compose?replyAll=mailbox/uid             → Antwort an alle
 *   /account/:id/compose?forward=mailbox/uid              → Weiterleiten
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api, ApiError, type EmailAccount, type FullMessage } from '../lib/api';
import { RichTextEditor } from '../components/RichTextEditor';

export function ComposePage() {
  const { accountId } = useParams<{ accountId: string }>();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reply = params.get('reply');
  const replyAll = params.get('replyAll');
  const forward = params.get('forward');
  const refSpec = reply || replyAll || forward; // "mailbox/uid"
  const mode = reply ? 'reply' : replyAll ? 'replyAll' : forward ? 'forward' : 'new';

  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [err, setErr] = useState('');
  const [origMessageId, setOrigMessageId] = useState<string | null>(null);
  const [origReferences, setOrigReferences] = useState<string[]>([]);

  // Aktueller Account
  const { data: accountData } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.get<{ accounts: EmailAccount[] }>('/accounts'),
  });
  const account = accountData?.accounts.find((a) => a.id === accountId);

  // Original-Mail laden für Reply/Forward
  useEffect(() => {
    if (!refSpec || !accountId) return;
    const slashIdx = refSpec.lastIndexOf('/');
    if (slashIdx < 0) return;
    const mb = refSpec.slice(0, slashIdx);
    const uid = refSpec.slice(slashIdx + 1);
    void api
      .get<{ message: FullMessage }>(
        `/accounts/${accountId}/mailboxes/${encodeURIComponent(mb)}/messages/${uid}`,
      )
      .then(({ message }) => {
        setOrigMessageId(message.messageId);
        const refs = [
          ...(message.references ?? []),
          ...(message.inReplyTo ? [message.inReplyTo] : []),
          ...(message.messageId ? [message.messageId] : []),
        ];
        setOrigReferences(refs);

        const formatQuote = (m: FullMessage): string => {
          const date = new Date(m.date).toLocaleString('de-DE');
          const from = m.fromName ? `${m.fromName} <${m.fromAddress}>` : m.fromAddress;
          const lines = (m.text ?? '').split('\n').map((l) => `> ${l}`).join('\n');
          return `\n\nAm ${date} schrieb ${from}:\n${lines}`;
        };

        if (mode === 'reply') {
          setTo(message.fromAddress);
          setSubject(`Re: ${message.subject.replace(/^(Re:\s*)+/i, '')}`);
          setBody(formatQuote(message));
        } else if (mode === 'replyAll') {
          const allRecipients = [
            message.fromAddress,
            ...message.toAddresses.map((a) => a.address),
            ...message.ccAddresses.map((a) => a.address),
          ].filter((a) => a && a !== account?.emailAddress);
          // doppelte raus
          setTo(Array.from(new Set(allRecipients)).join(', '));
          setSubject(`Re: ${message.subject.replace(/^(Re:\s*)+/i, '')}`);
          setBody(formatQuote(message));
        } else if (mode === 'forward') {
          setSubject(`Fwd: ${message.subject.replace(/^(Fwd:\s*)+/i, '')}`);
          setBody(
            `\n\n--- Weitergeleitete Nachricht ---\nVon: ${message.fromName ?? ''} <${message.fromAddress}>\nDatum: ${new Date(message.date).toLocaleString('de-DE')}\nBetreff: ${message.subject}\n\n${message.text ?? ''}`,
          );
        }
      })
      .catch((e) => setErr(e instanceof ApiError ? e.message : 'Original konnte nicht geladen werden'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refSpec, accountId, mode]);

  const send = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append('to', to);
      if (cc.trim()) fd.append('cc', cc);
      fd.append('subject', subject);
      // Body ist jetzt HTML vom Rich-Text-Editor
      // Plaintext-Fallback: HTML-Tags entfernen
      const plaintext = body.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
      fd.append('text', plaintext);
      fd.append('html', body);
      if (origMessageId) fd.append('inReplyTo', origMessageId);
      if (origReferences.length) fd.append('references', origReferences.join(','));
      for (const f of files) fd.append('file', f, f.name);

      const res = await fetch(`/api/v1/accounts/${accountId}/send`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
        throw new ApiError(res.status, j.error ?? 'SEND_FAILED', j.message ?? 'Senden fehlgeschlagen');
      }
      return (await res.json()) as { messageId: string };
    },
    onSuccess: () => {
      // Sicher zurück zum Posteingang navigieren
      nav(`/account/${accountId}/inbox`, { replace: true });
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Senden fehlgeschlagen'),
  });

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-3 flex items-center gap-2 text-sm">
        <Link to={`/account/${accountId}/inbox`} className="text-rla-700 hover:underline">
          ← Zurück
        </Link>
      </div>

      <h1 className="text-xl font-bold text-slate-800 mb-3">
        {mode === 'reply' && '↩ Antworten'}
        {mode === 'replyAll' && '↩↩ Allen antworten'}
        {mode === 'forward' && '➡ Weiterleiten'}
        {mode === 'new' && '✏ Neue Nachricht'}
      </h1>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErr('');
          if (!to.trim()) {
            setErr('Mindestens ein Empfänger erforderlich');
            return;
          }
          if (!subject.trim()) {
            setErr('Betreff erforderlich');
            return;
          }
          send.mutate();
        }}
        className="card space-y-3"
      >
        <div className="flex items-center text-sm">
          <label className="label w-12 mb-0">Von:</label>
          <div className="flex-1 text-slate-700">
            {account?.displayName
              ? `${account.displayName} <${account.emailAddress}>`
              : account?.emailAddress ?? '—'}
          </div>
        </div>

        <div className="flex items-start text-sm">
          <label className="label w-12 mb-0 mt-2">An:</label>
          <input
            type="text"
            className="input flex-1"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="empfaenger@beispiel.de"
            required
          />
          {!showCc && (
            <button
              type="button"
              onClick={() => setShowCc(true)}
              className="text-xs text-rla-700 hover:underline ml-2 mt-2"
            >
              + CC
            </button>
          )}
        </div>

        {showCc && (
          <div className="flex items-start text-sm">
            <label className="label w-12 mb-0 mt-2">CC:</label>
            <input
              type="text"
              className="input flex-1"
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              placeholder="kopie@beispiel.de, weiterer@beispiel.de"
            />
          </div>
        )}

        <div className="flex items-start text-sm">
          <label className="label w-12 mb-0 mt-2">Betreff:</label>
          <input
            type="text"
            className="input flex-1"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            required
          />
        </div>

        <RichTextEditor
          content={body}
          onChange={setBody}
          placeholder="Nachricht ..."
        />

        {/* Anhänge */}
        <div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            onChange={(e) => {
              const fs = Array.from(e.target.files ?? []);
              setFiles((prev) => [...prev, ...fs]);
              e.target.value = '';
            }}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="btn-secondary text-sm"
          >
            📎 Anhang hinzufügen
          </button>
          {files.length > 0 && (
            <ul className="mt-2 text-sm space-y-1">
              {files.map((f, i) => (
                <li key={i} className="flex items-center gap-2 bg-slate-50 rounded px-2 py-1">
                  <span>📄</span>
                  <span className="truncate flex-1">{f.name}</span>
                  <span className="text-xs text-slate-500">{Math.round(f.size / 1024)} KB</span>
                  <button
                    type="button"
                    onClick={() => setFiles(files.filter((_, idx) => idx !== i))}
                    className="text-red-500 hover:text-red-700"
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {err && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md p-3">
            {err}
          </div>
        )}

        <div className="flex gap-2 justify-end pt-2 border-t border-slate-200">
          <Link to={`/account/${accountId}/inbox`} className="btn-secondary">
            Abbrechen
          </Link>
          <button type="submit" disabled={send.isPending} className="btn-primary">
            {send.isPending ? 'Sende …' : '📤 Senden'}
          </button>
        </div>
      </form>
    </div>
  );
}
